import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors } from '../../styles/GlobalStyles';
import { classService } from '../../services/classService';
import { reservationService } from '../../services/reservationService';
import { memberService } from '../../services/memberService';
import { Button } from '../../components/Button';
import { Modal } from '../../components/Modal';
import { MemberCalendarView } from '../../components/MemberCalendarView';
import { TextArea, Label, FormGroup } from '../../components/Input';
import { Loader } from '../../components/Loader';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';

const Container = styled.div``;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 15px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 20px;
  }
`;

const Title = styled.h1`
  color: ${colors.primary};
  margin: 0;
`;

const ViewToggle = styled.div`
  display: flex;
  gap: 10px;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const ToggleButton = styled.button`
  padding: 8px 16px;
  border: 2px solid ${props => props.active ? colors.primary : colors.border};
  background: ${props => props.active ? colors.primary : 'white'};
  color: ${props => props.active ? 'white' : colors.text};
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${colors.primary};
    background: ${props => props.active ? colors.primary : colors.secondary};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  @media (max-width: 480px) {
    gap: 10px;
  }
`;

const Card = styled.div`
  background: ${props => props.$hasReservation ? '#e8f5e9' : 'white'};
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px ${colors.shadow};
  border: ${props => props.$hasReservation ? `3px solid ${colors.success}` : '1px solid #e0e0e0'};
  transition: transform 0.3s ease;
  position: relative;

  &:hover {
    transform: translateY(-4px);
  }

  @media (max-width: 768px) {
    padding: 15px;
  }

  @media (max-width: 480px) {
    padding: 12px;
  }

  ${props => props.$hasReservation && `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: ${colors.success};
      border-radius: 12px 12px 0 0;
    }
  `}
`;

const ClassTitle = styled.h3`
  color: ${props => props.$hasReservation ? colors.success : colors.primary};
  margin-bottom: 10px;
  font-weight: ${props => props.$hasReservation ? 'bold' : 'normal'};
`;

const ClassInfo = styled.div`
  color: ${colors.text};
  margin-bottom: 8px;
  font-size: 14px;
`;

const LocationLink = styled.a`
  color: ${colors.primary};
  text-decoration: none;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: ${colors.primaryDark};
    text-decoration: underline;
  }
`;

const ClassDescription = styled.p`
  color: ${colors.textLight};
  font-size: 14px;
  margin-bottom: 15px;
`;

const ReservationBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: ${colors.success};
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
  z-index: 1;
`;

const Progress = styled.div`
  width: 100%;
  height: 8px;
  background-color: ${colors.secondary};
  border-radius: 4px;
  overflow: hidden;
  margin: 10px 0;
`;

const ProgressBar = styled.div.attrs(props => ({
  percentage: undefined,
}))`
  height: 100%;
  background-color: ${props => {
    if (props.percentage >= 100) return colors.error;
    if (props.percentage >= 80) return colors.warning;
    return colors.success;
  }};
  width: ${props => props.percentage}%;
  transition: width 0.3s ease;
`;

const CapacityText = styled.div`
  font-size: 12px;
  color: ${colors.textLight};
  margin-bottom: 15px;
`;

const DaySection = styled.div`
  margin-bottom: 30px;
`;

const DayHeader = styled.div`
  background: ${colors.primary};
  color: white;
  padding: 15px 20px;
  border-radius: 8px 8px 0 0;
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 0;
`;

const DayGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  background: white;
  padding: 20px;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 2px 8px ${colors.shadow};
`;

const Alert = styled.div.attrs(props => ({
  type: undefined,
}))`
  background-color: ${props => {
    if (props.type === 'warning') return '#fff3cd';
    if (props.type === 'danger') return '#f8d7da';
    return '#d1ecf1';
  }};
  color: ${props => {
    if (props.type === 'warning') return '#856404';
    if (props.type === 'danger') return '#721c24';
    return '#0c5460';
  }};
  padding: 15px;
  border-radius: 6px;
  margin-bottom: 20px;
`;

export const MemberClasses = () => {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [memberData, setMemberData] = useState(null);
  const [myReservations, setMyReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [currentWeekStart, setCurrentWeekStart] = useState(getStartOfWeek(new Date()));

  // Mobilde otomatik olarak liste görünümü kullan
  useEffect(() => {
    const checkMobile = () => {
      if (window.innerWidth <= 768) {
        setViewMode('list');
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchData();
  }, [viewMode, currentWeekStart]); // Reload when view mode or week changes

  // Get start of week (Monday)
  function getStartOfWeek(date) {
    const day = date.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const monday = new Date(date);
    monday.setDate(date.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    return monday;
  }

  const fetchData = async () => {
    try {
      setLoading(true);
      // Use getAllClasses like admin panel does, then process in frontend
      // Also get weekly occupancy data to populate weekOccupancy
      const [allClasses, weeklyOccupancyData, member, reservations] = await Promise.all([
        classService.getAllClasses(),
        classService.getWeeklyOccupancy(currentWeekStart),
        memberService.getMember(user.id),
        reservationService.getMemberReservations(user.id)
      ]);
      
      // Merge weeklyOccupancy data into classes
      // getWeeklyOccupancy returns array of class instances for each day
      // Convert to weekOccupancy format: { classId: { dateStr: { currentBookings, availableSpots, isFull } } }
      const weekOccupancyMap = {};
      weeklyOccupancyData.forEach(classInstance => {
        if (!weekOccupancyMap[classInstance.id]) {
          weekOccupancyMap[classInstance.id] = {};
        }
        if (classInstance.date) {
          weekOccupancyMap[classInstance.id][classInstance.date] = {
            currentBookings: classInstance.currentBookings || 0,
            availableSpots: classInstance.availableSpots || (classInstance.capacity - (classInstance.currentBookings || 0)),
            isFull: classInstance.isFull || false
          };
        }
      });
      
      const classesWithOccupancy = allClasses.map(classItem => {
        if (weekOccupancyMap[classItem.id]) {
          return {
            ...classItem,
            weekOccupancy: weekOccupancyMap[classItem.id]
          };
        }
        return classItem;
      });
      
      setClasses(classesWithOccupancy);
      setMemberData(member);
      setMyReservations(reservations);
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Get classes for the current week (for list view) - same logic as admin panel
  const getWeekClasses = () => {
    const weekEnd = new Date(currentWeekStart);
    weekEnd.setDate(currentWeekStart.getDate() + 7);
    
    const expandedClasses = [];
    const now = new Date();
    const fiveHoursAgo = new Date(now.getTime() - (5 * 60 * 60 * 1000));
    
    // Calculate current week (Monday to Sunday) to show all days for current week
    const currentDay = now.getDay();
    const diffToMonday = currentDay === 0 ? -6 : 1 - currentDay;
    const todayWeekStart = new Date(now);
    todayWeekStart.setDate(now.getDate() + diffToMonday);
    todayWeekStart.setHours(0, 0, 0, 0);
    
    // Check if requested week is the current week
    const requestedWeekStartDate = new Date(currentWeekStart);
    requestedWeekStartDate.setHours(0, 0, 0, 0);
    const isCurrentWeek = todayWeekStart.getTime() === requestedWeekStartDate.getTime();
    
    classes.forEach(classItem => {
      if (classItem.isRecurring) {
        // For recurring classes, generate instances for each day in the week
        for (let i = 0; i < 7; i++) {
          const date = new Date(currentWeekStart);
          date.setDate(currentWeekStart.getDate() + i);
          date.setHours(0, 0, 0, 0);
          
          const classDate = new Date(classItem.date + 'T00:00:00');
          classDate.setHours(0, 0, 0, 0);
          
          // Only show if it's the same day of week and on or after the class start date
          if (date.getDay() === classDate.getDay() && date >= classDate) {
            const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            
            // Check if class is not too old (5 hours after class time)
            // For current week, show all days even if some classes are in the past
            const classTime = classItem.startTime || classItem.time || '00:00';
            const [hours, minutes] = classTime.split(':').map(Number);
            const classDateTime = new Date(date);
            classDateTime.setHours(hours, minutes || 0, 0, 0);
            
            if (isCurrentWeek || classDateTime >= fiveHoursAgo) {
              // Use the occupancy data from weekOccupancy for this specific date (same as admin panel)
              const occupancyData = classItem.weekOccupancy?.[dateStr] || {
                currentBookings: 0,
                availableSpots: classItem.capacity,
                isFull: false
              };
              
              expandedClasses.push({
                ...classItem,
                date: dateStr,
                displayDate: date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' }),
                currentBookings: occupancyData.currentBookings,
                availableSpots: occupancyData.availableSpots,
                isFull: occupancyData.isFull
              });
            }
          }
        }
      } else {
        // For non-recurring classes, check if it's in this week
        const classDate = new Date(classItem.date + 'T00:00:00');
        classDate.setHours(0, 0, 0, 0);
        const weekStartNormalized = new Date(currentWeekStart);
        weekStartNormalized.setHours(0, 0, 0, 0);
        const weekEndNormalized = new Date(weekEnd);
        weekEndNormalized.setHours(0, 0, 0, 0);
        
        if (classDate >= weekStartNormalized && classDate < weekEndNormalized) {
          // Check if class is not too old (5 hours after class time)
          // For current week, show all classes even if in the past
          const classTime = classItem.startTime || classItem.time || '00:00';
          const [hours, minutes] = classTime.split(':').map(Number);
          const classDateTime = new Date(classDate);
          classDateTime.setHours(hours, minutes || 0, 0, 0);
          
          if (isCurrentWeek || classDateTime >= fiveHoursAgo) {
            // Use the occupancy data that was already fetched with the class (same as admin panel)
            expandedClasses.push({
              ...classItem,
              displayDate: classDate.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' }),
              currentBookings: classItem.currentBookings || 0,
              availableSpots: classItem.availableSpots || (classItem.capacity - (classItem.currentBookings || 0)),
              isFull: classItem.isFull || false
            });
          }
        }
      }
    });
    
    // Sort by date and time
    return expandedClasses.sort((a, b) => {
      const dateCompare = (a.date || '').localeCompare(b.date || '');
      if (dateCompare !== 0) return dateCompare;
      return (a.startTime || a.time || '').localeCompare(b.startTime || b.time || '');
    });
  };

  // Get classes grouped by day for daily view - same logic as admin panel
  const getDailyClasses = () => {
    const weekClasses = getWeekClasses();
    const dailyGroups = {};
    
    weekClasses.forEach(classItem => {
      const date = classItem.date;
      if (!dailyGroups[date]) {
        dailyGroups[date] = [];
      }
      dailyGroups[date].push(classItem);
    });
    
    // Always create 7 days (Monday to Sunday) to ensure all days are shown
    const weekStart = new Date(currentWeekStart);
    weekStart.setHours(0, 0, 0, 0);
    const daysOfWeek = Array.from({ length: 7 }, (_, i) => {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + i);
      // Use local date string format to avoid timezone issues
      const year = day.getFullYear();
      const month = String(day.getMonth() + 1).padStart(2, '0');
      const dateNum = String(day.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${dateNum}`;
      
      return {
        date: dateStr,
        classes: (dailyGroups[dateStr] || []).sort((a, b) => (a.startTime || a.time || '').localeCompare(b.startTime || b.time || ''))
      };
    });
    
    return daysOfWeek;
  };

  // Check if a class is in the past or too close to start
  const isClassPast = (classDate, classTime) => {
    const now = new Date();
    const classDateTime = new Date(`${classDate}T${classTime || '00:00'}`);
    return classDateTime < now;
  };

  const goToPreviousWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(currentWeekStart.getDate() - 7);
    setCurrentWeekStart(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(currentWeekStart.getDate() + 7);
    setCurrentWeekStart(newDate);
  };

  const goToToday = () => {
    setCurrentWeekStart(getStartOfWeek(new Date()));
  };

  const getWeekRange = () => {
    const weekEnd = new Date(currentWeekStart);
    weekEnd.setDate(currentWeekStart.getDate() + 6);

    const startDay = currentWeekStart.getDate();
    const endDay = weekEnd.getDate();

    if (currentWeekStart.getMonth() === weekEnd.getMonth()) {
      const month = currentWeekStart.toLocaleDateString('tr-TR', { month: 'long' });
      const year = currentWeekStart.getFullYear();
      return `${startDay} - ${endDay} ${month} ${year}`;
    } else {
      const startMonth = currentWeekStart.toLocaleDateString('tr-TR', { month: 'long' });
      const endMonth = weekEnd.toLocaleDateString('tr-TR', { month: 'long' });
      const year = currentWeekStart.getFullYear();
      return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`;
    }
  };

  // Check if member has a pending or approved reservation for a class on a specific date
  const hasActiveReservation = (classId, classDate = null) => {
    const result = myReservations.some(r => {
      const matchesClass = r.classId === classId;
      const matchesStatus = r.status === 'pending' || r.status === 'approved';
      // If classDate is provided, also check if it matches
      const matchesDate = classDate ? r.classDate === classDate : true;
      
      return matchesClass && matchesStatus && matchesDate;
    });
    
    return result;
  };

  const handleOpenModal = (classItem) => {
    // Check if member already has a reservation for this class on this specific date
    if (hasActiveReservation(classItem.id, classItem.date)) {
      toast.error('Bu tarih için zaten bir rezervasyonunuz var.');
      return;
    }

    // Check if member can make reservation
    if (!memberData.isActive) {
      toast.error('Hesabınız pasif durumda');
      return;
    }

    const today = new Date();
    const membershipEnd = new Date(memberData.membershipEnd);
    if (membershipEnd < today) {
      toast.error('Üyeliğiniz sona ermiş');
      return;
    }

    // Check reservation limit only for reservation-based members
    if (memberData.definitionType === 'reservation') {
      if (memberData.reservationsUsed >= memberData.reservationLimit) {
        toast.error('Rezervasyon hakkınız dolmuş. Lütfen Seed Yoga Studio ile iletişime geçin.');
        return;
      }
    }

    // Allow reservation even if class is full - it will be pending for admin approval
    setSelectedClass(classItem);
    setNotes('');
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      
      const response = await reservationService.createReservation({
        classId: selectedClass.id,
        classDate: selectedClass.date, // Specific date for this reservation
        notes
      });
      
      // Show different message based on response status
      if (response.status === 'approved') {
        toast.success('Rezervasyonunuz başarıyla oluşturuldu!');
      } else {
        toast.info('Ders dolu. Rezervasyon talebiniz yöneticiye gönderildi.');
      }
      
      setModalOpen(false);
      // Refresh all data including reservations
      fetchData();
    } catch (error) {
      console.error('Reservation error:', error.response?.data);
      toast.error(error.response?.data?.error || 'Rezervasyon oluşturulamadı');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loader fullScreen />;
  }

  return (
    <Container>
      <Header>
        <Title>Ders Programı</Title>
        <ViewToggle>
          <ToggleButton 
            active={viewMode === 'list'} 
            onClick={() => setViewMode('list')}
          >
            📋 Liste Görünümü
          </ToggleButton>
          <ToggleButton 
            active={viewMode === 'calendar'} 
            onClick={() => setViewMode('calendar')}
            style={{ display: 'none' }}
            className="desktop-only"
          >
            📅 Takvim Görünümü
          </ToggleButton>
        </ViewToggle>
      </Header>

      {memberData?.definitionType === 'reservation' && memberData?.remainingReservations === 0 && (
        <Alert type="danger">
          ❌ Rezervasyon hakkınız dolmuş. Lütfen Seed Yoga Studio ile iletişime geçin.
        </Alert>
      )}

      {memberData?.definitionType === 'reservation' && memberData?.remainingReservations > 0 && memberData?.remainingReservations <= 2 && (
        <Alert type="warning">
          ⚠️ Kalan rezervasyon hakkınız: <strong>{memberData.remainingReservations}</strong>
        </Alert>
      )}

      {viewMode === 'list' ? (
        <>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '20px', 
            background: 'white', 
            padding: '15px', 
            borderRadius: '8px', 
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            flexWrap: 'wrap',
            gap: '10px'
          }}>
            <Button onClick={goToPreviousWeek} size="small" style={{ minWidth: '80px', fontSize: '12px', padding: '8px 12px' }}>← Önceki</Button>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: '8px', 
              flex: '1',
              minWidth: '140px',
              maxWidth: '200px'
            }}>
              <h3 style={{ 
                margin: 0, 
                color: '#2C5F2D', 
                fontSize: '16px', 
                textAlign: 'center', 
                fontWeight: '600',
                lineHeight: '1.2'
              }}>
                {getWeekRange()}
              </h3>
              <Button onClick={goToToday} size="small" variant="secondary" style={{ fontSize: '12px', padding: '6px 12px', minWidth: '60px' }}>Bugün</Button>
            </div>
            <Button onClick={goToNextWeek} size="small" style={{ minWidth: '80px', fontSize: '12px', padding: '8px 12px' }}>Sonraki →</Button>
          </div>
          {getDailyClasses().map((dayData, dayIndex) => {
            // Parse date string safely to avoid timezone issues
            const [year, month, day] = dayData.date.split('-').map(Number);
            const displayDate = new Date(year, month - 1, day);
            
            return (
            <DaySection key={dayData.date}>
              <DayHeader>
                {displayDate.toLocaleDateString('tr-TR', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric', 
                  weekday: 'long' 
                })}
              </DayHeader>
              <DayGrid>
                {dayData.classes.map((classItem, index) => {
                  const capacity = Number(classItem.capacity) || 0;
                  const percentage = capacity > 0
                    ? Math.min((classItem.currentBookings / capacity) * 100, 100)
                    : 0;
                  const hasReservation = hasActiveReservation(classItem.id, classItem.date);
                  const myReservation = myReservations.find(r => 
                    r.classId === classItem.id && 
                    r.classDate === classItem.date &&
                    (r.status === 'pending' || r.status === 'approved')
                  );
                  const isPast = isClassPast(classItem.date, classItem.startTime || classItem.time);
                  
                  return (
                    <Card 
                      key={`${classItem.id}-${classItem.date}`} 
                      style={{ opacity: isPast ? 0.6 : 1 }}
                      $hasReservation={hasReservation}
                    >
                      {hasReservation && (
                        <ReservationBadge>
                          {myReservation?.status === 'pending' ? '⏳ Onay Bekleniyor' : '✓ Rezerve'}
                        </ReservationBadge>
                      )}
                      <ClassTitle $hasReservation={hasReservation}>{classItem.title}</ClassTitle>
                      <ClassDescription>{classItem.description}</ClassDescription>
                      
                      <ClassInfo>👨‍🏫 <strong>Eğitmen:</strong> {classItem.instructor}</ClassInfo>
                      
                      {classItem.isOnline ? (
                        <ClassInfo>
                          💻 <strong>Online Ders</strong>
                          {classItem.onlineLink && hasReservation && myReservation?.status === 'approved' && (
                            <>
                              {' - '}
                              <LocationLink 
                                href={classItem.onlineLink}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Derse Katıl
                              </LocationLink>
                            </>
                          )}
                        </ClassInfo>
                      ) : classItem.location && (
                        <ClassInfo>
                          📍 <strong>Adres:</strong>{' '}
                          <LocationLink 
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(classItem.location)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {classItem.location}
                          </LocationLink>
                        </ClassInfo>
                      )}
                      <ClassInfo>🕐 <strong>Saat:</strong> {classItem.startTime || classItem.time} {classItem.endTime ? `- ${classItem.endTime}` : ''}</ClassInfo>

                      <Progress>
                        <ProgressBar percentage={percentage} />
                      </Progress>
                      <CapacityText>
                        {Math.max(classItem.availableSpots ?? (capacity - classItem.currentBookings), 0)} Boş Yer
                      </CapacityText>

                      {isPast && (
                        <ClassInfo style={{ color: colors.error, fontWeight: 'bold', marginTop: '10px' }}>
                          ⏰ Dersin saati geçti
                        </ClassInfo>
                      )}

                      {hasReservation && (
                        <ClassInfo style={{ color: colors.success, fontWeight: 'bold', marginTop: '10px' }}>
                          {myReservation?.status === 'pending' ? '⏳ Onay Bekleniyor' : '✓ Rezerve Edildi'}
                        </ClassInfo>
                      )}

                      <Button 
                        size="small" 
                        fullWidth 
                        onClick={() => handleOpenModal(classItem)}
                        disabled={isPast || !memberData?.isActive || hasReservation}
                        variant={hasReservation ? 'success' : (isPast ? 'secondary' : undefined)}
                      >
                        {isPast 
                          ? '⏰ Geçti'
                          : (hasReservation 
                            ? (myReservation?.status === 'pending' ? '⏳ Onay Bekleniyor' : '✓ Rezerve Edildi')
                            : (classItem.isFull ? '📝 Rezervasyon Talep Et' : '📝 Rezervasyon Yap')
                          )
                        }
                      </Button>
                    </Card>
                  );
                })}
              </DayGrid>
            </DaySection>
            );
          })}

          {getDailyClasses().length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', background: 'white', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <p style={{ color: colors.textLight }}>
                Bu hafta için ders bulunmamaktadır.
              </p>
            </div>
          )}
        </>
      ) : (
        <MemberCalendarView 
          classes={classes}
          myReservations={myReservations}
          onReservationClick={handleOpenModal}
        />
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={selectedClass?.isFull ? "Rezervasyon Talebi" : "Rezervasyon Yap"}
      >
        <ClassInfo style={{ marginBottom: '20px' }}>
          <strong>{selectedClass?.title}</strong><br />
          📅 {selectedClass?.date} - 🕐 {selectedClass?.startTime || selectedClass?.time}{selectedClass?.endTime ? ` - ${selectedClass?.endTime}` : ''}<br />
          👨‍🏫 {selectedClass?.instructor}
          {selectedClass?.isOnline ? (
            <>
              <br />
              💻 <strong>Online Ders</strong>
            </>
          ) : selectedClass?.location && (
            <>
              <br />
              📍{' '}
              <LocationLink 
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedClass.location)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {selectedClass.location}
              </LocationLink>
            </>
          )}
        </ClassInfo>

        {selectedClass?.isFull && (
          <Alert type="warning" style={{ marginBottom: '20px' }}>
            ⚠️ Bu ders dolu. Talebiniz yönetici onayına gönderilecek.
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Not (Opsiyonel)</Label>
            <TextArea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Varsa özel bir notunuz..."
            />
          </FormGroup>

          <Button type="submit" fullWidth disabled={submitting}>
            {submitting ? 'Gönderiliyor...' : (selectedClass?.isFull ? 'Talep Gönder' : 'Rezervasyon Yap')}
          </Button>
        </form>
      </Modal>
    </Container>
  );
};

